// src/contexts/auth/hooks/session.ts
/** @jsxImportSource @emotion/react */

import { useCallback } from "react";
import type { Session, SetAuthStateInput, User } from "../types";
import { makeAuthLogger } from "./storage";

import {
    authApi,
    profileApi,
    readStoredSession,
    writeStoredSession,
} from "../../../api";

import {
    clearSession as clearApiSession,
    setSession as setApiSession,
} from "../../../api/api";
import { writeAuthHandoffCookieFromSession } from "../cookie-session";

/**
 * IMPORTANT:
 * - This hook must NOT dispatch `auth:change` during normal state updates.
 * - `auth:change` should be reserved for "storage changed externally"
 *   OR exceptional flows where we intentionally clear/reset auth state.
 */
export const useAuthSession = (deps: {
    setSession: (v: Session | null) => void;
    setUser: (v: User | null) => void;
}) => {
    const { setSession, setUser } = deps;

    const L = makeAuthLogger("session");

    /**
     * Keep this for the interceptor / external token updates
     * (i.e. when *storage* changes but React state did not update yet).
     */
    const emitAuthChange = useCallback(() => {
        L.log("[emitAuthChange] dispatch auth:change");
        window.dispatchEvent(new Event("auth:change"));
    }, [L]);

    const setAuthStateFromTokens = useCallback(
        async (tokens: SetAuthStateInput) => {
            L.log("[setAuthStateFromTokens] start", {
                hasAccess: !!tokens?.access_token,
                hasRefresh: !!tokens?.refresh_token,
                hasUser: !!tokens?.user,
                expires_in: tokens?.expires_in,
                expires_at: tokens?.expires_at,
            });

            const access_token = tokens.access_token;
            const refresh_token = tokens.refresh_token;

            // ✅ exactOptionalPropertyTypes-safe: omit undefined optional keys
            const storagePayload: {
                access_token: string;
                refresh_token: string;
                expires_in?: number;
                expires_at?: number;
            } = { access_token, refresh_token };

            if (typeof tokens.expires_in === "number") {
                storagePayload.expires_in = tokens.expires_in;
            }
            if (typeof tokens.expires_at === "number") {
                storagePayload.expires_at = tokens.expires_at;
            }

            // ✅ single source storage
            writeStoredSession(storagePayload);

            // ✅ keep axios header in sync (NO emit)
            setApiSession(access_token, refresh_token, { emit: false });

            const nextSession: Session = {
                access_token,
                refresh_token,
                expires_in: typeof tokens.expires_in === "number"
                    ? tokens.expires_in
                    : 0,
                user: (tokens.user as User) ?? null,
            };

            // ✅ write handoff cookie (Safari tab ↔ standalone)
            // short-lived; safe to rewrite often
            try {
                writeAuthHandoffCookieFromSession(nextSession);
            } catch (e) {
                L.warn(
                    "[setAuthStateFromTokens] writeAuthHandoffCookie failed (ignored)",
                    e,
                );
            }

            // ✅ set in-memory state
            setSession(nextSession);
            setUser((tokens.user as User) ?? null);

            // ❗ DO NOT emit auth:change here (prevents loops + "refresh" flicker)

            // If backend didn't include user, fetch it once (still no emit).
            if (!tokens.user) {
                L.log("[setAuthStateFromTokens] no user -> GET /profiles/me");
                try {
                    const meUser = (await profileApi.getMe()) as any as User;

                    setUser(meUser ?? null);
                    setSession(
                        nextSession
                            ? { ...nextSession, user: meUser ?? null }
                            : null,
                    );

                    // keep cookie in sync with the user-enriched session
                    try {
                        writeAuthHandoffCookieFromSession({
                            ...nextSession,
                            user: meUser ?? null,
                        });
                    } catch {
                        // ignore
                    }
                } catch (e) {
                    L.warn("[setAuthStateFromTokens] /profiles/me failed", e);
                }
            }

            L.log("[setAuthStateFromTokens] done");
        },
        [L, setSession, setUser],
    );

    /**
     * Used by boot:
     * - if access token looks expired, refresh once
     *
     * Must NOT emit auth:change on success.
     * On failure: clear storage + state and emit once to rehydrate listeners.
     */
    const tryRefreshIfNeeded = useCallback(async () => {
        const { access_token, refresh_token, expires_at } = readStoredSession();

        L.log("[tryRefreshIfNeeded] start", {
            hasAccess: !!access_token,
            hasRefresh: !!refresh_token,
            expires_at,
        });

        if (!refresh_token) return null;

        try {
            L.log("[tryRefreshIfNeeded] calling authApi.refreshToken()");
            // authApi.refreshToken should update storage and axios headers in your api layer.
            // We return tokens so boot can proceed; we do not emit here.
            const tokens = await authApi.refreshToken();

            L.log("[tryRefreshIfNeeded] refresh OK", {
                hasAccess: !!(tokens as any)?.access_token,
                hasRefresh: !!(tokens as any)?.refresh_token,
            });

            // Optional: also update handoff cookie from latest stored tokens.
            // (Keeps standalone in sync after refresh.)
            try {
                const after = readStoredSession();
                if (after?.access_token && after?.refresh_token) {
                    writeAuthHandoffCookieFromSession({
                        access_token: after.access_token,
                        refresh_token: after.refresh_token,
                        expires_in: 0,
                        user: null,
                    });
                }
            } catch {
                // ignore
            }

            return tokens;
        } catch (e) {
            L.warn("[tryRefreshIfNeeded] refresh failed -> clear auth", e);

            // clear axios header + storage (NO emit here)
            clearApiSession({ emit: false });

            // clear in-memory
            setSession(null);
            setUser(null);

            // ✅ exceptional transition: tell listeners to rehydrate
            emitAuthChange();

            return null;
        }
    }, [L, emitAuthChange, setSession, setUser]);

    return {
        emitAuthChange,
        setAuthStateFromTokens,
        tryRefreshIfNeeded,
    };
};
